import React, { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Stack, Typography, Box } from "@mui/material";
import { useGetPhysicianGroupsByCompanyQuery } from "../../../services/ConfigurationService";
import { PhysicianGroupType } from "../../../services/ConfigurationService/types/physicianGroupType";
import { PhysicianGroupGrid } from "../components/grids/PhysicianGroupGrid";
import { PhysicianGroupDetailView } from "../components/views/PhysicianGroupDetailView";
import { PageLoading as Loading } from "../../common/components";
import PageCard from "../../common/components/styled-ui-elements/PageCard";
import { useGetMyProfileQuery } from "../../../services/ProfileService";
import { hasPhysicianGroupAccess } from "../../profile/utils/profileUtils";

const PAGE_STYLES = {
  container: {
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    minHeight: "100vh",
    padding: 3,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 3,
    borderBottom: "2px solid #3b82f6",
    backgroundColor: "#ffffff",
  },
  content: {
    padding: 3,
    backgroundColor: "#ffffff",
  },
};

function PhysicianGroups() {
  const { company } = useParams();
  const [selectedGroup, setSelectedGroup] = useState<PhysicianGroupType | null>(null);
  const { data: myProfile, isLoading: isProfileLoading } = useGetMyProfileQuery();
  const { data: groups = [], isLoading, error } = useGetPhysicianGroupsByCompanyQuery(
    { company: company || "" },
    { skip: !company }
  );

  // Route guard: redirect if user lacks PhysicianGroupEdit claim
  if (!isProfileLoading && !hasPhysicianGroupAccess(myProfile)) {
    return <Navigate to="/" replace />;
  }

  if (selectedGroup) {
    return (
      <PhysicianGroupDetailView
        group={selectedGroup}
        onBack={() => setSelectedGroup(null)}
      />
    );
  }

  return (
    <Box sx={PAGE_STYLES.container}>
      {isLoading ? (
        <Loading />
      ) : error ? (
        <PageCard>
          <Box sx={PAGE_STYLES.content}>
            <Typography color="error" variant="h6" align="center">
              Error loading physician groups
            </Typography>
          </Box>
        </PageCard>
      ) : (
        <PageCard>
          <Box sx={PAGE_STYLES.header}>
            <Typography variant="h5" sx={{ fontWeight: 600, color: "#1e293b" }}>
              Physician Groups
            </Typography>
          </Box>
          <Box sx={PAGE_STYLES.content}>
            <PhysicianGroupGrid
              groups={groups}
              company={company || ""}
              onViewMembers={(group) => setSelectedGroup(group)}
            />
          </Box>
        </PageCard>
      )}
    </Box>
  );
}

export { PhysicianGroups };
